/* eslint-disable react/no-multi-comp, react-hooks/exhaustive-deps, eqeqeq, @typescript-eslint/no-unused-vars  */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
// We're disabling rules of hooks in order to follow this practice in react-hook-form:
// https://react-hook-form.com/advanced-usage/#ConnectForm
import {
    ActionIcon,
    Avatar,
    Box,
    CloseButton,
    FileButton,
    Group,
    Input,
    Checkbox as MantineCheckbox,
    Chip as MantineChip,
    MultiSelect as MantineMultiSelect,
    NumberInput as MantineNumberInput,
    Radio as MantineRadio,
    SegmentedControl as MantineSegmentedControl,
    Select as MantineSelect,
    Switch as MantineSwitch,
    TextInput as MantineTextInput,
    Text,
    Tooltip,
    useMantineTheme,
} from "@mantine/core"
import axios from "axios"
import React, { FC, ReactNode, useEffect, useState } from "react"
import { FieldError, UseControllerProps, useController, useFormContext } from "react-hook-form"

import type {
    CheckboxGroupProps,
    CheckboxProps,
    ChipGroupProps,
    ChipProps,
    FileButtonProps,
    MultiSelectProps,
    NumberInputProps,
    RadioGroupProps,
    RadioProps,
    SegmentedControlProps,
    SelectProps,
    SwitchProps,
    TextInputProps,
    TextareaProps,
} from "@mantine/core"

import {
    DatePickerInput as MantineDatePickerInput,
    TimeInput as MantineTimeInput,
    YearPickerInput as MantineYearPickerInput,
} from "@mantine/dates"

import type { DatePickerInputProps, TimeInputProps } from "@mantine/dates"

import { AvatarUpload, Icon, MultiSelectItem } from "@/components"

import { useStyles } from "@/components/Controlled/Controlled.styles"
import { TitleInput as CoreTitleInput, TitleInputProps } from "@/components/Controlled/TitleInput"
import { AtMentionTextarea as QuorumAtMentionTextarea } from "app/static/frontend/forms/components/AtMentionTextarea"
import { downloadCenterPaths } from "app/static/frontend/imports/desktopPaths"
import { ErrorMessages } from "./ErrorMessages"

export const Label: FC<{
    label: ReactNode | string
    className?: string
    tooltip?: string | ReactNode
    withAsterisk?: boolean
}> = ({ label, tooltip, withAsterisk, className }) => {
    const theme = useMantineTheme()
    const { classes } = useStyles()
    const renderLabel = () => {
        return (
            <>
                <Text color={theme.colors.gray[7]} component="span" className={className}>
                    {label}
                </Text>
                {tooltip && (
                    <Tooltip label={tooltip}>
                        <Icon
                            ariaLabel={tooltip?.toString()}
                            color={theme.colors.gray[7]}
                            icon="info-circle"
                            size="1x"
                        />
                    </Tooltip>
                )}
                {withAsterisk && (
                    <Text className={classes.labelAsterisk} component="span" aria-label="required">
                        *
                    </Text>
                )}
            </>
        )
    }

    return (
        <>
            {withAsterisk && <div className={classes.labelContainer}>{renderLabel()}</div>}
            {!withAsterisk && renderLabel()}
        </>
    )
}

// DEPRECATED: use <ErrorMessages> component instead
export const renderFieldError = (fieldError: FieldError) => <ErrorMessages fieldError={fieldError} />

export const ConnectForm = ({ children }) => {
    const formContext = useFormContext()
    return children(formContext)
}

export type ControlledComponentProps<MantineComponentProps> = UseControllerProps &
    MantineComponentProps & {
        label?: string
        /** Where possible, use 'rules { required: "Reason" }' to identify required fields instead. */
        required?: boolean
        /** Where possible, use 'rules { required: "Reason" }' to identify required fields instead. */
        withAsterisk?: boolean
        tooltip?: string
        transform?: {
            input?: (value: any) => any
            output?: (value: any) => any
        }
    }

const CheckboxIcon: CheckboxProps["icon"] = ({ indeterminate, className }) =>
    indeterminate ? <Icon icon="minus" className={className} /> : <Icon icon="check" className={className} />

export const Checkbox = (props: CheckboxProps) => <MantineCheckbox {...props} icon={CheckboxIcon} />

const CheckboxGroup = ({
    name,
    defaultValue,
    label,
    rules,
    shouldUnregister,
    tooltip,
    transform,
    ...props
}: ControlledComponentProps<CheckboxGroupProps & { placeholder?: string }>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { onChange, value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            return (
                <MantineCheckbox.Group
                    label={label && <Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    id={name}
                    value={transform ? transform.input(value) : value}
                    onChange={(output) => onChange(transform ? transform.output(output) : output)}
                    {...props}
                    {...field}
                />
            )
        }}
    </ConnectForm>
)

Checkbox.Group = CheckboxGroup

export const Chip = ({ name, ...props }: ControlledComponentProps<ChipProps>) => (
    <ConnectForm>
        {({ control }) => {
            const { field } = useController({ name, control })

            return <MantineChip {...props} {...field} />
        }}
    </ConnectForm>
)

const ChipGroup = ({
    name,
    ...props
}: ControlledComponentProps<Omit<ChipGroupProps, "multiple"> & { multiple: boolean }>) => (
    <ConnectForm>
        {({ control }) => {
            const { field } = useController({ name, control })
            const { ref: fieldRef, ...restFieldProps } = field

            return (
                // ChipGroup doesn't forward ref, so we pass it to Input.Wrapper
                <Input.Wrapper ref={fieldRef}>
                    <MantineChip.Group {...props} {...restFieldProps} />
                </Input.Wrapper>
            )
        }}
    </ConnectForm>
)

Chip.Group = ChipGroup

// TODO: This component is a hot mess, and we should build a new one when we have time.
export const AtMentionTextarea = ({
    label,
    name,
    tooltip,
    rules,
    ...props
}: ControlledComponentProps<TextareaProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field,
                fieldState: { error },
            } = useController({ name, control, rules })

            return (
                <div style={{ position: "relative" }}>
                    <QuorumAtMentionTextarea
                        useMantineTextarea
                        displayAtButton={false}
                        label={<Label label={label} tooltip={tooltip} />}
                        error={error && renderFieldError(error)}
                        id={name}
                        {...props}
                        {...field}
                        // adding disabled here, field is overriding props with undefined
                        disabled={props.disabled}
                    />
                </div>
            )
        }}
    </ConnectForm>
)

export const TextInput = ({
    name,
    defaultValue,
    initializingFn,
    label,
    rules,
    shouldUnregister,
    tooltip,
    onChange,
    onBlur,
    readOnly,
    withAsterisk,
    value,
    showError = true,
    ...props
}: ControlledComponentProps<TextInputProps> & { initializingFn?: (node, onChange) => void; showError?: boolean }) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field,
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })
            const refCallback = (node) => {
                field.ref(node)
                initializingFn && initializingFn(node, field.onChange)
            }
            const className = readOnly ? `${props.className} read-only` : props.className
            const required = !readOnly && (props.required || withAsterisk || Boolean(rules?.required))
            const asteriskStatus = !readOnly && !required && withAsterisk
            const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(event.target.value)
                if (onChange) onChange(event)
            }

            return (
                <MantineTextInput
                    label={label && <Label label={label} tooltip={tooltip} withAsterisk={!!asteriskStatus} />}
                    error={error && showError ? renderFieldError(error) : null}
                    aria-invalid={Boolean(error)}
                    id={name}
                    {...field}
                    {...props}
                    className={className}
                    readOnly={readOnly}
                    onChange={handleOnChange}
                    required={required}
                    ref={refCallback}
                    value={value ?? field.value}
                    onBlur={onBlur}
                />
            )
        }}
    </ConnectForm>
)

export const NumberInput = ({
    name,
    defaultValue,
    label,
    readOnly = false,
    rules,
    shouldUnregister,
    tooltip,
    transform,
    ...props
}: ControlledComponentProps<NumberInputProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { onChange, value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })
            const className = readOnly ? `${props.className} read-only` : props.className
            const required = !readOnly && (props.required || Boolean(rules?.required))
            return (
                <MantineNumberInput
                    label={<Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    id={name}
                    value={transform ? transform.input(value) : value}
                    onChange={(output) => onChange(transform ? transform.output(output) : output)}
                    {...props}
                    {...field}
                    className={className}
                    readOnly={readOnly}
                    required={required}
                    // adding disabled here, field is overriding props with undefined
                    disabled={props.disabled}
                />
            )
        }}
    </ConnectForm>
)

export const TitleInput = ({
    name,
    defaultValue,
    rules,
    shouldUnregister,
    transform,
    ...props
}: ControlledComponentProps<TitleInputProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { onChange, value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            return (
                <CoreTitleInput
                    aria-invalid={Boolean(error)}
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    defaultValue={defaultValue}
                    {...props}
                    {...field}
                />
            )
        }}
    </ConnectForm>
)

export const SegmentedControl = ({
    name,
    defaultValue,
    onChange,
    ...props
}: ControlledComponentProps<SegmentedControlProps>) => {
    const { classes } = useStyles()
    return (
        <ConnectForm>
            {({ control }) => {
                const { field } = useController({ name, control, defaultValue })

                return (
                    <MantineSegmentedControl
                        classNames={{ control: classes.segmentButtonWrapper, label: classes.segmentButtonLabel }}
                        name={name}
                        defaultValue={defaultValue}
                        {...props}
                        {...field}
                        onChange={(value) => {
                            if (onChange) onChange(value)
                            field.onChange(value)
                        }}
                    />
                )
            }}
        </ConnectForm>
    )
}

export const Select = ({
    name,
    defaultValue,
    label,
    readOnly = false,
    rules,
    shouldUnregister,
    tooltip,
    transform,
    onChange,
    ...props
}: ControlledComponentProps<SelectProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })
            const className = readOnly ? `${props.className} read-only` : props.className
            const required = !readOnly && (props.required || Boolean(rules?.required))
            const handleOnChange = (value: string) => {
                field.onChange(transform ? transform.output(value) : value)
                if (onChange) onChange(value)
            }
            return (
                <MantineSelect
                    aria-invalid={Boolean(error)}
                    clearButtonProps={{ "aria-label": "Clear selection" }}
                    error={error && renderFieldError(error)}
                    id={name}
                    label={label && <Label label={label} tooltip={tooltip} />}
                    value={transform ? transform.input(value) : value}
                    {...props}
                    {...field}
                    className={className}
                    readOnly={readOnly}
                    required={required}
                    onChange={handleOnChange}
                    // adding disabled here, field is overriding props with undefined
                    disabled={props.disabled}
                />
            )
        }}
    </ConnectForm>
)

export const MultiSelect = ({
    name,
    defaultValue,
    label,
    rules,
    shouldUnregister,
    tooltip,
    transform,
    ...props
}: ControlledComponentProps<MultiSelectProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { onChange, value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            return (
                <MantineMultiSelect
                    aria-invalid={Boolean(error)}
                    clearButtonProps={{ "aria-label": "Clear selections" }}
                    error={error && renderFieldError(error)}
                    id={name}
                    itemComponent={MultiSelectItem}
                    label={label && <Label label={label} tooltip={tooltip} />}
                    searchable
                    onChange={(output) => onChange(transform ? transform.output(output) : output)}
                    value={transform ? transform.input(value) : value}
                    {...props}
                    {...field}
                    // adding disabled here, field is overriding props with undefined
                    disabled={props.disabled}
                />
            )
        }}
    </ConnectForm>
)

export type ControlledRadioGroupProps<MantineComponentProps> = Omit<
    ControlledComponentProps<MantineComponentProps>,
    "label"
> & {
    label?: ReactNode | string
}

const RadioGroup = ({
    name,
    defaultValue,
    label,
    rules,
    shouldUnregister,
    tooltip,
    transform,
    onChange,
    ...props
}: ControlledRadioGroupProps<RadioGroupProps>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field: { value, ...field },
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            const handleOnChange = (value: string) => {
                field.onChange(transform ? transform.output(value) : value)
                if (onChange) onChange(value)
            }

            return (
                <MantineRadio.Group
                    label={label && <Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    id={name}
                    value={transform ? transform.input(value) : value}
                    wrapperProps={{ role: "radiogroup", "aria-labelledby": `${name}-label` }}
                    {...props}
                    {...field}
                    onChange={handleOnChange}
                />
            )
        }}
    </ConnectForm>
)

export const Radio = (props: RadioProps) => <MantineRadio {...props} />

Radio.Group = RadioGroup

export const Switch = ({
    name,
    defaultValue,
    label,
    readOnly = false,
    rules,
    shouldUnregister,
    tooltip,
    disabled,
    ...props
}: ControlledComponentProps<SwitchProps>) => {
    const { classes } = useStyles()
    return (
        <ConnectForm>
            {({ control, watch, getValues }) => {
                const {
                    field,
                    fieldState: { error },
                } = useController({
                    name,
                    control,
                    defaultValue,
                    rules,
                    shouldUnregister,
                })
                const className = readOnly
                    ? `${props.className} ${classes.switch} read-only`
                    : `${props.className} ${classes.switch}`
                const required = !readOnly && (props.required || Boolean(rules?.required))

                const handleOutput = (output = false) =>
                    props?.transform?.output ? props.transform?.output(output) : output
                const handleInput = (input = false) => (props?.transform?.input ? props.transform?.input(input) : input)

                return (
                    <MantineSwitch
                        {...props}
                        {...field}
                        className={className}
                        label={<Label label={label} tooltip={tooltip} className={classes.switchLabel} />}
                        error={error && renderFieldError(error)}
                        aria-invalid={Boolean(error)}
                        id={name}
                        onChange={(e) => {
                            field.onChange(handleOutput(!field.value))
                            if (props.onChange) props.onChange(e)
                        }}
                        checked={handleInput(Boolean(field.value) ?? false)}
                        readOnly={readOnly}
                        disabled={disabled}
                        required={required}
                    />
                )
            }}
        </ConnectForm>
    )
}

export const DatePickerInput = ({
    name,
    defaultValue,
    label,
    readOnly = false,
    rules,
    shouldUnregister,
    tooltip,
    ...props
}: ControlledComponentProps<DatePickerInputProps & { placeholder?: string }>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field,
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })
            const className = readOnly ? `${props.className} read-only` : props.className
            const required = !readOnly && (props.required || Boolean(rules?.required))
            return (
                <MantineDatePickerInput
                    {...props}
                    {...field}
                    label={<Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    icon={<Icon icon="calendar" />}
                    id={name}
                    valueFormat="MM/DD/YYYY"
                    defaultValue={new Date()}
                    className={className}
                    readOnly={readOnly}
                    required={required}
                    // adding disabled here, field is overriding props with undefined
                    disabled={props.disabled}
                />
            )
        }}
    </ConnectForm>
)

export const YearPickerInput = ({
    name,
    defaultValue,
    label,
    rules,
    shouldUnregister,
    tooltip,
    ...props
}: ControlledComponentProps<DatePickerInputProps & { placeholder?: string }>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field,
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            return (
                <MantineYearPickerInput
                    label={<Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    icon={<Icon icon="calendar" />}
                    id={name}
                    defaultValue={new Date()}
                    {...props}
                    {...field}
                />
            )
        }}
    </ConnectForm>
)

export const TimeInput = ({
    name,
    clearable,
    defaultValue,
    label,
    rules,
    shouldUnregister,
    tooltip,
    renderLabel = true,
    ...props
}: ControlledComponentProps<TimeInputProps & { clearable?: boolean; renderLabel?: boolean }>) => (
    <ConnectForm>
        {({ control }) => {
            const {
                field,
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            const { resetField } = useFormContext()

            /* Mantine's TimeInput isn't clearable. There are probably some reasons why,
             * including the fact that browsers render time input controls differently.
             *
             * Regardless, we're making it clearable. To do that we need to know the
             * state of the field and hide/show the clear button accordingly.
             *
             * Additionally, we need to account for an engineer passing through some of
             * the props that are available: rightSection, readOnly, and disabled. Using
             * the clearable prop won't do anything if any of those props are set.
             */
            const [isClearable, setIsClearable] = useState(clearable && field.value?.length > 0)
            useEffect(() => {
                setIsClearable(clearable && field.value?.length > 0)
            }, [field.value])

            const rightSection =
                props.rightSection || (isClearable && !props.readOnly && !props.disabled) ? (
                    <CloseButton
                        aria-label="Clear field"
                        onClick={() => resetField(field.name, { defaultValue: "" })}
                        variant="transparent"
                    />
                ) : null

            return (
                <MantineTimeInput
                    label={renderLabel && <Label label={label} tooltip={tooltip} />}
                    error={error && renderFieldError(error)}
                    aria-invalid={Boolean(error)}
                    icon={<Icon icon="clock" />}
                    id={name}
                    rightSection={rightSection}
                    {...props}
                    {...field}
                    // adding disabled here, field is overriding props with undefined
                    disabled={props.disabled}
                />
            )
        }}
    </ConnectForm>
)

export const ProfileUploadButton = ({
    label,
    name,
    onChange,
    ...props
}: ControlledComponentProps<FileButtonProps> & { validationRules?: any; profileURL?: any }) => (
    <ConnectForm>
        {({ control }) => {
            const { field } = useController({ name, control, rules: props.validationRules })
            const { classes } = useStyles()
            const [error, setError] = useState<FieldError | null>(null)
            const theme = useMantineTheme()
            const formContext = useFormContext()

            field.onChange = (file) => {
                const data: any = new FormData()
                data.append(0, file)
                axios
                    .post(downloadCenterPaths.uploadAttachment, data, {
                        headers: {
                            Accept: "application/json",
                            "Accept-Language": "en-US,en;q=0.8",
                            "Content-Type": `multipart/form-data; boundary=${data._boundary}`,
                        },
                        params: {}, // pass an empty object for params so axios interceptor doesn't complain
                    })
                    .then((response) => {
                        const image_url = response[0].url
                        formContext.setValue("profileUpload", image_url)
                        setError(null)

                        if (onChange) {
                            onChange(image_url)
                        }
                    })
                    .catch((responseError) => {
                        console.error(responseError)
                        setError({
                            type: "manual",
                            message:
                                "Invalid file. Ensure your profile picture is a JPEG or PNG with a maximum file size of 10MB.",
                        })
                    })
            }
            return (
                <label className={classes.profileLabel}>
                    <Label label="Profile Picture" tooltip={props.tooltip} />
                    {error && renderFieldError(error)}
                    <FileButton accept="image/png,image/jpeg" {...field}>
                        {(fileButtonProps: any) => (
                            <Group className={classes.profileButton} spacing={0} align="end">
                                <Avatar
                                    alt="Upload profile picture"
                                    className={classes.profileIcon}
                                    radius={128}
                                    size={128}
                                    src={fileButtonProps.value || props.profileURL}
                                    {...fileButtonProps}
                                >
                                    {!props.profileURL && (
                                        <Box className={classes.stylizedIcon}>
                                            <Icon icon="avatar" iconFamily="q" color={theme.colors.primary[4]} />
                                        </Box>
                                    )}
                                </Avatar>
                                <ActionIcon
                                    aria-label={`${props.profileURL ? "Change" : "Upload"} profile picture`}
                                    className={classes.uploadIcon}
                                    radius={32}
                                    size={32}
                                    {...fileButtonProps}
                                >
                                    <Icon color={theme.colors.primary[8]} icon="arrow-from-bottom" iconFamily="fas" />
                                </ActionIcon>
                            </Group>
                        )}
                    </FileButton>
                </label>
            )
        }}
    </ConnectForm>
)

export const CaptureFileInput = ({ label, name, className }) => (
    <ConnectForm>
        {({ control }) => {
            const { field } = useController({
                name,
                control,
            })

            const handleFileChange = (e) => {
                e.target.files.length > 0 && field.onChange(e.target.files[0])
            }

            return (
                <label className={className}>
                    {label}
                    <input
                        {...field}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileChange}
                        value=""
                        className="capture-file-input"
                    />
                </label>
            )
        }}
    </ConnectForm>
)

export const AvatarUploadField = (props) => (
    <ConnectForm>
        {({ control }) => {
            const { field } = useController({
                name: props.name,
                control,
            })

            return <AvatarUpload {...props} {...field} />
        }}
    </ConnectForm>
)
